/**
 * Class used to render svgs/images on a Zubie map.
 */
class MapImage {
  id: string;

  content: string | null = null;

  options: {height?: number; type?: 'svg+xml'; width?: number} = {
    height: 0,
    type: 'svg+xml',
    width: 0,
  };

  constructor(
    id: string,
    content: string,
    options: typeof this.options = {height: 0, type: 'svg+xml', width: 0}
  ) {
    if (!id) {
      throw new Error('MapImage requires an id');
    }

    this.id = id;
    this.content = content;
    this.options = {
      ...this.options,
      ...options,
    };
  }

  /**
   * Returns the substring of the content (assumes it beings with "base64:").
   */
  get base64String() {
    return this.content?.substring(7);
  }

  /**
   * Returns the options->height
   */
  get height() {
    return this.options.height;
  }

  /**
   * Returns the substring of the content (assumes it beings with "img:").
   */
  get imageUrl() {
    const url = this.content?.substring(4);

    if (url?.startsWith('https://')) {
      return url;
    }

    return `/${url}`;
  }

  /**
   * Used to identify Base64 encoded images.
   */
  get isBase64() {
    return this.content?.startsWith('base64:');
  }

  /**
   * Used to identify image URLs.
   */
  get isImageUrl() {
    return this.content?.startsWith('img:');
  }

  /**
   * Returns the options->width
   */
  get width() {
    return this.options.width;
  }

  /**
   * Loads the content (as needed) and returns a Promise that resolves with
   * the image ID and the created Image.
   */
  load(mapInstance: mapboxgl.Map) {
    const {id, content} = this;

    return new Promise<{
      id: string;
      image: HTMLImageElement | ImageBitmap | ImageData | null | undefined;
    }>((resolve, reject) => {
      if (!content) {
        reject(new Error('Map image is empty or undefined'));
        return;
      }

      if (this.isImageUrl) {
        // Handle image URLs
        mapInstance.loadImage(this.imageUrl, (error, image) => {
          if (error) {
            reject(error);
          } else {
            resolve({id, image});
          }
        });
      } else {
        // Handle Base64/SVG markup
        const {width, height} = this;
        const image = new Image(width, height);
        const imageData = this.isBase64
          ? this.base64String
          : window.btoa(content);
        image.src = `data:image/${this.options.type};base64,${imageData}`;
        image.onload = () => {
          resolve({id, image});
        };
      }
    });
  }
}

export default MapImage;
